export const BRANCH_SCHEDULES_SAVED_EVENT = "talento-humano:branch-schedules-saved";

export function notifyBranchSchedulesSaved(
  branchId: number,
  weekIndex?: number | null,
) {
  window.dispatchEvent(
    new CustomEvent(BRANCH_SCHEDULES_SAVED_EVENT, {
      detail: { branchId, weekIndex: weekIndex ?? undefined },
    }),
  );
}

export function normalizeScheduleSlotLabel(s: unknown): string {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function isScheduleItemCoveringEssentialSlot(
  scheduleItem: any,
  positionRow: {
    positionName?: string;
    shift?: string;
    positionOriginalId_?: number;
  },
): boolean {
  if (!scheduleItem?.show) return false;
  if (positionRow.shift != null && scheduleItem.shift !== positionRow.shift) {
    return false;
  }
  const slot = normalizeScheduleSlotLabel(positionRow.positionName);
  const assigned = normalizeScheduleSlotLabel(scheduleItem.position);
  if (slot.length > 0 && assigned.length > 0) {
    return assigned === slot;
  }
  return (
    Number(scheduleItem.positionOriginalId_) ===
    Number(positionRow.positionOriginalId_)
  );
}

export function normalizePositionIdKey(posId: unknown): string {
  if (posId == null || posId === "") return "";
  const n = Number(posId);
  if (!Number.isNaN(n)) return String(n);
  return String(posId).trim();
}

/** Clave de slot: colaborador + día + puesto (por nombre; al mover en táctica cambia positionId). */
export function partnerDayScheduleSlotKey(
  partnerId: number,
  day: string,
  opts: { positionId?: unknown; positionName?: string },
): string {
  const label = normalizeScheduleSlotLabel(opts.positionName ?? "");
  if (label) return `${partnerId}|${day}|n:${label}`;
  return `${partnerId}|${day}|p:${normalizePositionIdKey(opts.positionId)}`;
}

export function branchScheduleRecordSlotKey(
  s: any,
  getPartnerId: (s: any) => number | undefined,
): string | null {
  const pid = getPartnerId(s);
  if (pid == null || !s?.dayOfWeek) return null;
  return partnerDayScheduleSlotKey(pid, s.dayOfWeek, {
    positionId: s.position?.id ?? s.position,
    positionName: s.positionName || s.position?.name || "",
  });
}

export function weeklySaveItemSlotKey(it: any): string {
  return partnerDayScheduleSlotKey(it.partnerId, it.day, {
    positionId: it.positionId,
    positionName: it.positionDisplayName || it.positionName || "",
  });
}

function shiftKey(shift: unknown): string {
  return String(shift ?? "")
    .trim()
    .toLowerCase();
}

export function dedupeWeeklyTableItems(items: any[]): any[] {
  const out: any[] = [];
  const seen = new Set<string>();
  for (const it of items) {
    const rowSid = Number(
      (it as any).branchScheduleId ?? (it as any).scheduleRowId ?? 0,
    );
    if (!Number.isNaN(rowSid) && rowSid > 0) {
      const k = `sid:${rowSid}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(it);
      continue;
    }
    const sk = shiftKey(it.shift);
    const isDescanso =
      it.positionId == null &&
      (it.positionDisplayName === "Descanso" ||
        String(it.positionName || "").toLowerCase().includes("descanso"));
    if (isDescanso) {
      const k = `${it.partnerId}|${it.day}|${sk}|DESCANSO`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(it);
      continue;
    }
    const k = `${it.partnerId}|${it.day}|${sk}|${normalizePositionIdKey(it.positionId)}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(it);
  }
  return out;
}

function pickPreferredDisplayItem(a: any, b: any): any {
  const aId = Number(a?.id ?? a?.branchScheduleId ?? 0);
  const bId = Number(b?.id ?? b?.branchScheduleId ?? 0);
  const aBlock =
    a?.positionW != null && Number(a.positionW) > 0;
  const bBlock =
    b?.positionW != null && Number(b.positionW) > 0;
  if (aBlock && !bBlock) return a;
  if (bBlock && !aBlock) return b;
  if (aId > 0 && bId <= 0) return a;
  if (bId > 0 && aId <= 0) return b;
  return bId > aId ? b : a;
}

/** Evita tarjetas duplicadas cuando táctica cambió positionId pero el puesto es el mismo. */
export function dedupeDisplayItemsForPartnerDay(
  items: any[],
  partnerId: number,
  day: string,
): any[] {
  const slotMap = new Map<string, any>();
  for (const item of items) {
    const key = partnerDayScheduleSlotKey(partnerId, day, {
      positionId: item.position?.id ?? item.positionId,
      positionName: item.positionName || item.position?.name || "",
    });
    const existing = slotMap.get(key);
    slotMap.set(
      key,
      existing ? pickPreferredDisplayItem(item, existing) : item,
    );
  }
  return Array.from(slotMap.values());
}

function pickPreferredBranchScheduleRecord(a: any, b: any): any {
  const aHasBlock =
    a.positionX != null &&
    a.positionW != null &&
    Number(a.positionW) > 0;
  const bHasBlock =
    b.positionX != null &&
    b.positionW != null &&
    Number(b.positionW) > 0;
  if (aHasBlock && !bHasBlock) return a;
  if (bHasBlock && !aHasBlock) return b;
  const aid = Number(a.id) || 0;
  const bid = Number(b.id) || 0;
  return bid > aid ? b : a;
}

/** Una tarjeta por colaborador+puesto+día (un solo BranchSchedule por slot). */
export function dedupeBranchSchedulesByPartnerDayPosition(
  list: any[],
  getPartnerId: (s: any) => number | undefined,
): any[] {
  const slotMap = new Map<string, any>();
  for (const s of list) {
    const key = branchScheduleRecordSlotKey(s, getPartnerId);
    if (!key) continue;
    const existing = slotMap.get(key);
    slotMap.set(
      key,
      existing ? pickPreferredBranchScheduleRecord(s, existing) : s,
    );
  }
  return Array.from(slotMap.values());
}

/** @deprecated Usar dedupeBranchSchedulesByPartnerDayPosition */
export function dedupeBranchSchedulesForDisplay(
  list: any[],
  _normalizeShift: (shift: any) => string,
  getPartnerId: (s: any) => number | undefined,
): any[] {
  return dedupeBranchSchedulesByPartnerDayPosition(list, getPartnerId);
}

export function normalizeWeeklyCellPartLabel(value: string): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

/** Partes de celda semanal: "Puesto A, Puesto B" o "Puesto A (Vespertino)". */
export function splitWeeklyCellParts(cellValue: string): string[] {
  const raw = String(cellValue ?? "").trim();
  if (!raw || raw === "-" || raw === "DESCANSO") return [];
  return raw
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
}

export function weeklyCellPartMatchesScheduleName(
  cellPart: string,
  scheduleName: string,
): boolean {
  const part = normalizeWeeklyCellPartLabel(cellPart);
  const name = normalizeWeeklyCellPartLabel(scheduleName);
  if (!part || !name) return false;
  return (
    name === part ||
    name.startsWith(part + " ") ||
    part.startsWith(name + " ")
  );
}

export function appendWeeklyCellPart(
  cellValue: string,
  part: string,
): string {
  const next = String(part ?? "").trim();
  if (!next) return String(cellValue ?? "").trim();
  const existing = splitWeeklyCellParts(cellValue);
  if (existing.some((p) => p.toLowerCase() === next.toLowerCase())) {
    return existing.join(", ");
  }
  return existing.length ? `${existing.join(", ")}, ${next}` : next;
}

export function replaceWeeklyCellPartsForShift(
  cellValue: string,
  rowShift: string,
  newPartsForShift: string[],
  belongsToShift: (part: string, shift: string) => boolean,
): string {
  const kept = splitWeeklyCellParts(cellValue).filter(
    (part) => !belongsToShift(part, rowShift),
  );
  const added = newPartsForShift.map((p) => p.trim()).filter(Boolean);
  const merged = [...kept, ...added];
  return merged.join(", ");
}

/** Reemplaza una sola etiqueta de puesto en la celda (p. ej. al hacer clic en una tarjeta concreta). */
export function replaceOneWeeklyCellPart(
  cellValue: string,
  targetPartLabel: string,
  newPart: string,
): string {
  const target = String(targetPartLabel ?? "").trim();
  const parts = splitWeeklyCellParts(cellValue);
  if (!target) {
    const next = String(newPart ?? "").trim();
    return next;
  }
  let removed = false;
  const kept = parts.filter((part) => {
    if (!removed && weeklyCellPartMatchesScheduleName(part, target)) {
      removed = true;
      return false;
    }
    return true;
  });
  const next = String(newPart ?? "").trim();
  if (next) kept.push(next);
  return kept.join(", ");
}

export function countWeeklyCellPartsForShift(
  cellValue: string,
  rowShift: string,
  belongsToShift: (part: string, shift: string) => boolean,
): number {
  return splitWeeklyCellParts(cellValue).filter((part) =>
    belongsToShift(part, rowShift),
  ).length;
}

function preferWeeklySaveItem(a: any, b: any): any {
  const aSid = Number(a?.branchScheduleId ?? a?.scheduleRowId ?? 0);
  const bSid = Number(b?.branchScheduleId ?? b?.scheduleRowId ?? 0);
  if (aSid > 0 && bSid <= 0) return a;
  if (bSid > 0 && aSid <= 0) return b;
  if (aSid > 0 && bSid > 0) return bSid > aSid ? b : a;
  return b;
}

/** Un item de guardado por colaborador+puesto+día (nombre de puesto, no solo positionId). */
export function dedupeWeeklyItemsByPartnerDayPosition(items: any[]): any[] {
  const slotMap = new Map<string, any>();
  for (const it of items) {
    const isDescanso =
      it.positionId == null &&
      (it.positionDisplayName === "Descanso" ||
        String(it.positionName || "").toLowerCase().includes("descanso"));
    if (isDescanso) {
      const k = `${it.partnerId}|${it.day}|DESCANSO`;
      if (!slotMap.has(k)) slotMap.set(k, it);
      continue;
    }
    if (it.positionId == null || it.positionId === "") continue;
    const key = weeklySaveItemSlotKey(it);
    const existing = slotMap.get(key);
    slotMap.set(key, existing ? preferWeeklySaveItem(it, existing) : it);
  }
  return Array.from(slotMap.values());
}

export function dedupePartnerDayShiftPositionItems(items: any[]): any[] {
  return dedupeWeeklyItemsByPartnerDayPosition(items);
}

export type WeeklySaveCellContext = {
  cellAssignments: Record<string, string>;
  initialCellAssignments: Record<string, string>;
  removedScheduleIds: number[];
};

/** El semanal ya define esta plaza en este turno → no conservar registro paralelo. */
export function isWeeklyDesiredCoveringScheduleSlot(
  schedule: any,
  desiredItems: any[],
): boolean {
  const partnerId = Number(schedule.partner?.id ?? schedule.partner);
  const posId = Number(schedule.position?.id ?? schedule.position);
  const schedShift = normalizeShiftKeyForSave(schedule.shift);
  return desiredItems.some((it) => {
    if (Number(it.partnerId) !== partnerId) return false;
    if (Number(it.positionId) !== posId) return false;
    return normalizeShiftKeyForSave(it.shift) === schedShift;
  });
}

/** Conservar actividades de táctica que el guardado semanal no debe borrar. */
export function shouldPreserveTacticalScheduleOnWeeklySave(
  schedule: any,
  day: string,
  desiredItems: any[],
  weeklyCtx: WeeklySaveCellContext,
): boolean {
  const sid = Number(schedule?.id);
  if (!Number.isNaN(sid) && sid > 0) {
    if (weeklyCtx.removedScheduleIds.includes(sid)) return false;
  }

  if (isWeeklyDesiredCoveringScheduleSlot(schedule, desiredItems)) {
    return false;
  }

  const partnerId = Number(schedule.partner?.id ?? schedule.partner);
  const cellKey = `${partnerId}-${day}`;
  const currentCell = (weeklyCtx.cellAssignments[cellKey] ?? "").trim();
  const initialCell = (weeklyCtx.initialCellAssignments[cellKey] ?? "").trim();
  const isEmptyCell = (v: string) => !v || v === "-";

  // Si la grilla semanal ya muestra algo para este colaborador/día, solo mandan los desired.
  if (currentCell && currentCell !== "DESCANSO") {
    return false;
  }

  if (currentCell === "DESCANSO") return false;
  if (isEmptyCell(currentCell) && !isEmptyCell(initialCell)) return false;

  return true;
}

export function mapExistingScheduleToWeeklySavePayload(
  schedule: any,
  opts: {
    weekValue: string;
    day: string;
    shiftName: string;
    exactDateStr: string;
    branchId: number;
  },
): any {
  const { weekValue, day, shiftName, exactDateStr, branchId } = opts;
  return {
    dateRange: schedule.dateRange || weekValue,
    dayOfWeek: day,
    positionName: schedule.positionName,
    positionX: schedule.positionX,
    positionW: schedule.positionW,
    positionY: schedule.positionY ?? 0,
    startTime: schedule.startTime,
    endTime: schedule.endTime,
    exactDate: exactDateStr,
    branch: branchId,
    partner: schedule.partner?.id ?? schedule.partner,
    position: schedule.position?.id ?? schedule.position,
    shift: shiftName,
    positionOriginalId_:
      schedule.originalPositionId ??
      schedule.position?.id ??
      schedule.position,
    originalRecordId: schedule.id,
  };
}

/** Une asignaciones semanales con actividades ya guardadas en táctica (SheduleComponent). */
export function mergeWeeklySaveWithExistingSchedules(
  scheduleRecordsFromDesired: any[],
  existingForDayShift: any[],
  desiredItems: any[],
  weeklyCtx: WeeklySaveCellContext,
  opts: {
    weekValue: string;
    day: string;
    shiftName: string;
    exactDateStr: string;
    branchId: number;
  },
): any[] {
  const coveredIds = new Set<number>();
  for (const r of scheduleRecordsFromDesired) {
    const oid = Number(r?.originalRecordId);
    if (!Number.isNaN(oid) && oid > 0) coveredIds.add(oid);
  }

  const preserved = existingForDayShift
    .filter((s) => {
      const sid = Number(s?.id);
      if (!Number.isNaN(sid) && sid > 0 && coveredIds.has(sid)) return false;
      return shouldPreserveTacticalScheduleOnWeeklySave(
        s,
        opts.day,
        desiredItems,
        weeklyCtx,
      );
    })
    .map((s) => mapExistingScheduleToWeeklySavePayload(s, opts));

  return dedupeScheduleRecordsForSave([
    ...scheduleRecordsFromDesired,
    ...preserved,
  ]);
}

function normalizeShiftKeyForSave(shift: any): string {
  if (shift == null || shift === "") return "";
  if (typeof shift === "string") return shift.trim().toLowerCase();
  if (typeof shift === "object" && (shift as { name?: string }).name != null) {
    return String((shift as { name: string }).name).trim().toLowerCase();
  }
  return String(shift).trim().toLowerCase();
}

/** Enlaza filas nuevas con BranchSchedule existente (mismo colaborador+puesto+turno). */
export function attachMissingOriginalRecordIds(
  records: any[],
  existingForDayShift: any[],
): any[] {
  if (!existingForDayShift.length) return records;
  const usedIds = new Set<number>();
  for (const r of records) {
    const oid = Number(r?.originalRecordId);
    if (!Number.isNaN(oid) && oid > 0) usedIds.add(oid);
  }
  return records.map((r) => {
    const oid = Number(r?.originalRecordId);
    if (!Number.isNaN(oid) && oid > 0) return r;
    const partnerId = Number(r.partner);
    const posId = Number(r.position);
    const shiftKey = normalizeShiftKeyForSave(r.shift);
    const wantName = normalizeScheduleSlotLabel(r.positionName || "");
    const match = existingForDayShift.find((s) => {
      const sid = Number(s?.id);
      if (Number.isNaN(sid) || sid <= 0 || usedIds.has(sid)) return false;
      if (Number(s.partner?.id ?? s.partner) !== partnerId) return false;
      if (normalizeShiftKeyForSave(s.shift) !== shiftKey) return false;
      const sPos = Number(s.position?.id ?? s.position);
      if (sPos === posId) return true;
      if (!wantName) return false;
      const sName = normalizeScheduleSlotLabel(
        s.positionName || s.position?.name || "",
      );
      return !!sName && sName === wantName;
    });
    if (!match) return r;
    usedIds.add(Number(match.id));
    return {
      ...r,
      originalRecordId: match.id,
      positionX: r.positionX ?? match.positionX,
      positionW: r.positionW ?? match.positionW,
      positionY: r.positionY ?? match.positionY ?? 0,
      startTime: match.startTime || r.startTime,
      endTime: match.endTime || r.endTime,
    };
  });
}

/** Una fila por colaborador+puesto+día en el payload de guardado. */
export function dedupeScheduleRecordsForSave(records: any[]): any[] {
  const seenOriginal = new Set<number>();
  const slotMap = new Map<string, any>();
  for (const r of records) {
    const oid = r?.originalRecordId != null ? Number(r.originalRecordId) : NaN;
    if (!Number.isNaN(oid) && oid > 0) {
      if (seenOriginal.has(oid)) continue;
      seenOriginal.add(oid);
    }
    const slotKey = `${r.partner}|${r.dayOfWeek}|${normalizeScheduleSlotLabel(r.positionName || "") || normalizePositionIdKey(r.position)}`;
    const existing = slotMap.get(slotKey);
    if (!existing) {
      slotMap.set(slotKey, r);
      continue;
    }
    const rHasOid = !Number.isNaN(oid) && oid > 0;
    const eOid = Number(existing.originalRecordId);
    const eHasOid = !Number.isNaN(eOid) && eOid > 0;
    if (rHasOid && !eHasOid) {
      slotMap.set(slotKey, r);
    } else if (rHasOid && eHasOid && oid > eOid) {
      slotMap.set(slotKey, r);
    }
  }
  return Array.from(slotMap.values());
}
